import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  BookOpen, Plus, Loader2, ChevronRight, BarChart3, MessageSquare,
  Globe, Layers,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/i18n";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CourseEditor from "@/components/CourseEditor";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
const initials = (name = "") => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";

export default function TeacherDashboard() {
  const { t } = useI18n();
  const [courses, setCourses] = useState([]);
  const [editCourseId, setEditCourseId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState([]);

  const [courseForm, setCourseForm] = useState({ title: "", description: "", level: "A1", language: "en" });
  const [courseOpen, setCourseOpen] = useState(false);

  const loadCourses = async () => {
    try {
      const [c, p] = await Promise.all([api.get("/courses"), api.get("/teacher/progress")]);
      setCourses(c.data); setProgress(p.data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadCourses(); }, []);

  const createCourse = async (e) => {
    e.preventDefault();
    try {
      await api.post("/courses", courseForm);
      toast.success(t("teacher.new_course")); setCourseOpen(false);
      setCourseForm({ title: "", description: "", level: "A1", language: "en" });
      loadCourses();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const saveComment = async (subId, comment) => {
    try { await api.post(`/submissions/${subId}/comment`, { comment }); toast.success(t("teacher.save_feedback")); loadCourses(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const tabCls = "rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-5 py-2.5 font-semibold";

  return (
    <AppLayout>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <Tabs defaultValue="courses" className="space-y-8">
          <TabsList className="rounded-full bg-muted p-1.5 h-auto">
            <TabsTrigger value="courses" data-testid="tab-courses" className={tabCls}><BookOpen className="h-4 w-4 mr-2" />{t("nav.courses")}</TabsTrigger>
            <TabsTrigger value="progress" data-testid="tab-progress" className={tabCls}><BarChart3 className="h-4 w-4 mr-2" />{t("nav.progress")}</TabsTrigger>
          </TabsList>

          <TabsContent value="courses" className="space-y-7">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="font-heading text-4xl font-extrabold tracking-tight flex items-center gap-3 text-primary">
                  <span className="h-11 w-11 rounded-2xl bg-primary/5 ring-1 ring-secondary/30 flex items-center justify-center"><Layers className="h-6 w-6 text-secondary" /></span>
                  {t("teacher.my_courses")}
                </h1>
                <p className="text-muted-foreground mt-2 text-base">{t("teacher.my_courses_sub")}</p>
              </div>
              <Dialog open={courseOpen} onOpenChange={setCourseOpen}>
                <DialogTrigger asChild><Button data-testid="open-create-course-button" size="lg" className="rounded-full font-bold h-12 px-6"><Plus className="h-5 w-5 mr-2" />{t("teacher.new_course")}</Button></DialogTrigger>
                <DialogContent className="rounded-2xl">
                  <DialogHeader><DialogTitle className="text-2xl">{t("teacher.new_course")}</DialogTitle></DialogHeader>
                  <form onSubmit={createCourse} className="space-y-4">
                    <div className="space-y-2"><Label>{t("common.title")}</Label>
                      <Input data-testid="course-title-input" required value={courseForm.title} onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })} className="rounded-xl h-11" /></div>
                    <div className="space-y-2"><Label>{t("common.description")}</Label>
                      <Textarea value={courseForm.description} onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })} className="rounded-xl" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2"><Label>{t("common.level")}</Label>
                        <Select value={courseForm.level} onValueChange={(v) => setCourseForm({ ...courseForm, level: v })}>
                          <SelectTrigger className="rounded-xl h-11"><SelectValue /></SelectTrigger>
                          <SelectContent>{LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select></div>
                      <div className="space-y-2"><Label>{t("common.language")}</Label>
                        <Select value={courseForm.language} onValueChange={(v) => setCourseForm({ ...courseForm, language: v })}>
                          <SelectTrigger className="rounded-xl h-11"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="en">English</SelectItem><SelectItem value="es">Español</SelectItem></SelectContent></Select></div>
                    </div>
                    <DialogFooter><Button type="submit" data-testid="submit-course-button" className="rounded-full h-11 px-6">{t("common.create")}</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-secondary" /></div> : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {courses.map((c) => (
                  <Card key={c.id} data-testid={`course-card-${c.id}`} className="rounded-[1.4rem] p-7 cursor-pointer border-border hover:-translate-y-1.5 transition-transform shadow-[0_6px_24px_rgb(20,38,77,0.05)] group" onClick={() => setEditCourseId(c.id)}>
                    <div className="flex items-start justify-between mb-5">
                      <div className="h-14 w-14 rounded-2xl bg-primary/5 ring-1 ring-secondary/30 flex items-center justify-center group-hover:bg-secondary/15 transition-colors"><BookOpen className="h-7 w-7 text-secondary" /></div>
                      <div className="flex flex-col items-end gap-1.5"><Badge className="rounded-full font-bold">{c.level}</Badge><Badge variant="outline" className="rounded-full uppercase font-semibold"><Globe className="h-3 w-3 mr-1" />{c.language}</Badge></div>
                    </div>
                    <h3 className="font-heading text-xl font-bold">{c.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">{c.description}</p>
                    <div className="flex items-center text-secondary text-sm font-bold mt-5">{t("teacher.manage")} <ChevronRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" /></div>
                  </Card>
                ))}
                {courses.length === 0 && <p className="text-muted-foreground col-span-full text-center py-10">—</p>}
              </div>
            )}
          </TabsContent>

          <TabsContent value="progress" className="space-y-6">
            <h1 className="font-heading text-4xl font-extrabold tracking-tight text-primary flex items-center gap-3">
              <span className="h-11 w-11 rounded-2xl bg-primary/5 ring-1 ring-secondary/30 flex items-center justify-center"><BarChart3 className="h-6 w-6 text-secondary" /></span>
              {t("nav.progress")}
            </h1>
            {progress.length === 0 ? <p className="text-muted-foreground">{t("teacher.no_submissions")}</p> : (
              <div className="grid lg:grid-cols-2 gap-5">
                {progress.map((s) => (
                  <Card key={s.id} data-testid={`submission-${s.id}`} className="rounded-[1.4rem] p-6 border-border shadow-[0_6px_24px_rgb(20,38,77,0.05)]">
                    <div className="flex items-center gap-3">
                      <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground font-heading font-bold flex items-center justify-center shrink-0">{initials(s.student_name)}</div>
                      <div className="flex-1 min-w-0">
                        <span className="font-heading font-bold truncate block">{s.student_name}</span>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="rounded-full text-[11px]">{t(`ex.${s.exercise_type}`)}</Badge>
                          {s.grade && <Badge variant="secondary" className="rounded-full text-[11px]">{t(`grade.${s.grade}`)}</Badge>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-heading text-2xl font-extrabold text-secondary">{s.score ?? "—"}</div>
                        <div className="text-[10px] text-muted-foreground">/100</div>
                      </div>
                    </div>
                    {s.transcript && <p className="text-sm mt-3 text-muted-foreground italic">"{s.transcript}"</p>}
                    <div className="flex gap-2 mt-4">
                      <Input defaultValue={s.teacher_comment} placeholder={t("teacher.comment")} className="rounded-xl" id={`cmt-${s.id}`} data-testid={`comment-input-${s.id}`} />
                      <Button size="sm" className="rounded-full shrink-0" data-testid={`save-comment-${s.id}`}
                        onClick={() => saveComment(s.id, document.getElementById(`cmt-${s.id}`).value)}>
                        <MessageSquare className="h-4 w-4 mr-1" />{t("common.save")}
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </motion.div>

      <CourseEditor courseId={editCourseId} onClose={() => setEditCourseId(null)} />
    </AppLayout>
  );
}
